import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Calculator,
  Loader2,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { api, clearAdminToken, getAdminToken } from "@/lib/api";
import { METHOD_DEFS } from "@/lib/eligibility";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(+n || 0));
const fmtINRShort = (n) => {
  const v = Math.round(+n || 0);
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000) return `₹${(v / 1_00_000).toFixed(2)} L`;
  if (v >= 1_000) return `₹${(v / 1_000).toFixed(1)}K`;
  return `₹${v}`;
};

// Field metadata per method — controls input UI (currency / pct / months)
const FIELD_META = {
  // currency
  avgMonthlyCredits: { label: "Avg monthly credits in current A/C", kind: "inr" },
  annualTurnover: { label: "Annual GST turnover (last 12 mo)", kind: "inr" },
  propertyValue: { label: "Property fair value", kind: "inr" },
  monthlyIncome: { label: "Monthly income (optional)", kind: "inr" },
  outstandingPrincipal: { label: "Outstanding principal", kind: "inr" },
  annualRevenue: { label: "Annual revenue", kind: "inr" },
  annualCOGS: { label: "Annual COGS", kind: "inr" },
  annualGrossReceipts: { label: "Annual gross receipts", kind: "inr" },
  netMonthlyTakeHome: { label: "Net monthly take-home", kind: "inr" },
  projectedAnnualEBITDA: { label: "Projected annual EBITDA (stabilisation)", kind: "inr" },
  existingEMI: { label: "Existing monthly EMIs", kind: "inr" },
  // percent
  assumedMarginPct: { label: "Assumed profit margin", kind: "pct" },
  foirPct: { label: "FOIR cap", kind: "pct" },
  ltvPct: { label: "LTV ratio", kind: "pct" },
  topUpPct: { label: "Top-up % over OS", kind: "pct" },
  currentRatePct: { label: "Current loan rate", kind: "pct" },
  newRatePct: { label: "New loan rate (post BT)", kind: "pct" },
  presumptivePct: { label: "Presumptive income (44ADA)", kind: "pct" },
  interestRatePct: { label: "Indicative loan rate", kind: "pct" },
  // months / count
  tenureMonths: { label: "Tenure (months)", kind: "months" },
  remainingTenureMonths: { label: "Remaining tenure (months)", kind: "months" },
  cleanRTRMonths: { label: "Months of clean RTR", kind: "months" },
  multiplierMonths: { label: "Multiplier (months of take-home)", kind: "months" },
  // ratio
  targetDSCR: { label: "Target DSCR", kind: "ratio" },
  thumbMultiplier: { label: "Rule-of-thumb multiplier (× turnover)", kind: "ratio" },
};

function NumberField({ k, value, onChange, kind, label }) {
  const meta = FIELD_META[k] || {};
  const finalKind = kind || meta.kind || "inr";
  const finalLabel = label || meta.label || k;
  const display =
    finalKind === "inr"
      ? new Intl.NumberFormat("en-IN").format(value || 0)
      : finalKind === "pct"
      ? value
      : value;
  const handle = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (finalKind === "ratio") {
      const v = parseFloat(raw);
      onChange(isNaN(v) ? 0 : v);
    } else {
      const v = parseFloat(raw.replace(/[^\d.]/g, ""));
      onChange(isNaN(v) ? 0 : v);
    }
  };
  const suffix = finalKind === "pct" ? "%" : finalKind === "months" ? "mo" : finalKind === "ratio" ? "" : null;
  return (
    <div className="space-y-1.5" data-testid={`elig-field-${k}`}>
      <Label className="text-xs font-medium text-brand-forest">{finalLabel}</Label>
      <div className="relative">
        {finalKind === "inr" && (
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-sage text-sm">₹</span>
        )}
        <Input
          value={display}
          onChange={handle}
          className={`h-10 ${finalKind === "inr" ? "pl-7" : ""} ${suffix ? "pr-9" : ""}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-sage text-xs">
            {suffix}
          </span>
        )}
      </div>
      {finalKind === "inr" && value > 0 && (
        <div className="text-[11px] text-brand-sage">≈ {fmtINRShort(value)}</div>
      )}
    </div>
  );
}

function ResultCard({ method, result, isBest }) {
  return (
    <div
      data-testid={`elig-result-${method.key}`}
      className={`rounded-2xl p-5 transition-all ${
        isBest
          ? "bg-brand-forest text-brand-cream ring-2 ring-brand-gold shadow-[0_24px_60px_-30px_rgba(228,168,83,0.6)]"
          : "border border-brand-sand bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`text-[10px] uppercase tracking-[0.16em] font-semibold ${isBest ? "text-brand-gold" : "text-brand-sage"}`}>
            {method.short}
          </div>
          <div className={`mt-1 font-outfit font-extrabold text-2xl ${isBest ? "text-brand-cream" : "text-brand-forest"}`}>
            {fmtINRShort(result.eligibility)}
          </div>
          <div className={`mt-0.5 text-[11px] ${isBest ? "text-brand-cream/70" : "text-brand-sage"}`}>
            ₹{fmtINR(result.eligibility)}
          </div>
        </div>
        {isBest && (
          <Badge className="bg-brand-gold text-brand-forest font-semibold border-0">
            <Award className="h-3 w-3 mr-1" /> Best
          </Badge>
        )}
      </div>
      {result.monthlyAffordableEMI > 0 && (
        <div className={`mt-3 text-xs ${isBest ? "text-brand-cream/75" : "text-brand-sage"}`}>
          Affordable EMI · {fmtINRShort(result.monthlyAffordableEMI)}/mo
        </div>
      )}
    </div>
  );
}

export default function AdminEligibility() {
  const navigate = useNavigate();
  const [active, setActive] = useState(METHOD_DEFS[0].key);
  const [inputs, setInputs] = useState(() =>
    Object.fromEntries(METHOD_DEFS.map((m) => [m.key, { ...m.defaults }]))
  );
  const [client, setClient] = useState({ name: "", phone: "", email: "", notes: "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    loadHistory();
  }, [navigate]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/eligibility");
      setHistory(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
      }
    }
  };

  const setField = (methodKey, fieldKey, val) => {
    setInputs((prev) => ({
      ...prev,
      [methodKey]: { ...prev[methodKey], [fieldKey]: val },
    }));
  };

  // Compute all 8 results live
  const results = useMemo(() => {
    const out = {};
    for (const m of METHOD_DEFS) {
      try {
        out[m.key] = m.calc(inputs[m.key]);
      } catch (e) {
        out[m.key] = { eligibility: 0, monthlyAffordableEMI: 0, computedMonthlyIncome: 0, notes: "", breakdown: [] };
      }
    }
    return out;
  }, [inputs]);

  const best = useMemo(() => {
    let bestKey = null;
    let bestVal = 0;
    for (const m of METHOD_DEFS) {
      if (results[m.key].eligibility > bestVal) {
        bestVal = results[m.key].eligibility;
        bestKey = m.key;
      }
    }
    return { key: bestKey, value: bestVal };
  }, [results]);

  const save = async () => {
    if (!client.name) {
      toast.error("Enter the client name first.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/eligibility", {
        client_name: client.name,
        client_phone: client.phone || null,
        client_email: client.email || null,
        client_notes: client.notes || null,
        inputs,
        results,
        best_method: best.key,
        best_eligibility: best.value,
      });
      toast.success("Assessment saved");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const loadAssessment = (a) => {
    setClient({
      name: a.client_name || "",
      phone: a.client_phone || "",
      email: a.client_email || "",
      notes: a.client_notes || "",
    });
    if (a.inputs) {
      const next = Object.fromEntries(METHOD_DEFS.map((m) => [m.key, { ...m.defaults, ...(a.inputs[m.key] || {}) }]));
      setInputs(next);
    }
    if (a.best_method) setActive(a.best_method);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`Loaded — ${a.client_name}`);
  };

  const removeAssessment = async (id) => {
    if (!window.confirm("Delete this saved assessment? This cannot be undone.")) return;
    try {
      await api.delete(`/eligibility/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const activeMethod = METHOD_DEFS.find((m) => m.key === active);
  const activeResult = results[active];
  const activeInputs = inputs[active];

  return (
    <div data-testid="admin-eligibility-page" className="container-x py-10 md:py-14">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="eyebrow">Admin · Eligibility Calculator</span>
          <h1 className="mt-3 h-display text-3xl sm:text-4xl">
            Loan eligibility across 8 lender methods.
          </h1>
          <p className="mt-3 text-brand-sage max-w-2xl">
            Punch in client numbers — see all 8 methods compute live, with the best-fit highlighted. Adjust assumptions per method as the client's profile dictates.
          </p>
        </div>
        <Link to="/admin/dashboard" className="btn-ghost">
          <ArrowLeft className="h-4 w-4" /> Back to leads
        </Link>
      </div>

      {/* Best result banner */}
      <div data-testid="elig-best-banner" className="mt-8 rounded-2xl bg-brand-forest text-brand-cream p-6 md:p-7 relative overflow-hidden">
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-terracotta/25 blur-2xl" />
        <div className="absolute -left-8 bottom-0 h-32 w-32 rounded-full bg-brand-gold/20 blur-2xl" />
        <div className="relative grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-gold font-semibold">Highest eligibility across methods</div>
            <div className="mt-2 font-outfit font-extrabold text-4xl sm:text-5xl tracking-tight">
              {fmtINRShort(best.value)}
            </div>
            <div className="mt-2 text-brand-cream/80">
              via <strong className="text-brand-gold">{METHOD_DEFS.find((m) => m.key === best.key)?.title || "—"}</strong>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-brand-cream/10 border border-brand-cream/15 p-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-brand-cream/60">Median across methods</div>
              <div className="mt-1 font-outfit font-bold text-lg">
                {fmtINRShort(median(METHOD_DEFS.map((m) => results[m.key].eligibility).filter((x) => x > 0)))}
              </div>
            </div>
            <div className="rounded-xl bg-brand-cream/10 border border-brand-cream/15 p-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-brand-cream/60">Methods with eligibility &gt; 0</div>
              <div className="mt-1 font-outfit font-bold text-lg">
                {METHOD_DEFS.filter((m) => results[m.key].eligibility > 0).length} / {METHOD_DEFS.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Side-by-side comparison */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="elig-grid">
        {METHOD_DEFS.map((m) => (
          <button
            key={m.key}
            onClick={() => setActive(m.key)}
            data-testid={`elig-grid-${m.key}`}
            className={`text-left transition-all ${active === m.key ? "scale-[1.02]" : ""}`}
          >
            <ResultCard method={m} result={results[m.key]} isBest={m.key === best.key} />
          </button>
        ))}
      </div>

      {/* Tabs with detailed inputs */}
      <Tabs value={active} onValueChange={setActive} className="mt-10">
        <TabsList className="flex-wrap h-auto bg-brand-sand/40 p-1.5">
          {METHOD_DEFS.map((m) => (
            <TabsTrigger
              key={m.key}
              value={m.key}
              data-testid={`elig-tab-${m.key}`}
              className="text-xs sm:text-sm data-[state=active]:bg-brand-forest data-[state=active]:text-brand-cream"
            >
              {m.short}
            </TabsTrigger>
          ))}
        </TabsList>

        {METHOD_DEFS.map((m) => (
          <TabsContent key={m.key} value={m.key} className="mt-6">
            <div className="grid lg:grid-cols-12 gap-6">
              {/* Inputs */}
              <div className="lg:col-span-7 card-soft p-6">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-forest text-brand-gold">
                    <Calculator className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-outfit font-bold text-xl text-brand-forest">{m.title}</h3>
                    <p className="text-xs text-brand-sage">{m.suitable}</p>
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {Object.entries(activeInputs || {}).map(([fieldKey, val]) => (
                    <NumberField
                      key={fieldKey}
                      k={fieldKey}
                      value={val}
                      onChange={(v) => setField(m.key, fieldKey, v)}
                    />
                  ))}
                </div>

                <button
                  onClick={() =>
                    setInputs((prev) => ({ ...prev, [m.key]: { ...m.defaults } }))
                  }
                  className="mt-6 text-xs font-medium text-brand-terracotta hover:underline"
                >
                  Reset to industry defaults
                </button>
              </div>

              {/* Result + breakdown */}
              <aside className="lg:col-span-5">
                <div className="card-soft p-6 bg-brand-forest text-brand-cream">
                  <div className="text-xs uppercase tracking-[0.18em] text-brand-gold">Eligibility — this method</div>
                  <div className="mt-2 font-outfit font-extrabold text-4xl">
                    {fmtINRShort(activeResult.eligibility)}
                  </div>
                  <div className="text-xs text-brand-cream/65 mt-1">₹{fmtINR(activeResult.eligibility)}</div>

                  <div className="mt-5 space-y-2">
                    {(activeResult.breakdown || []).map((b, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className={`text-brand-cream/70 ${b.bold ? "font-semibold text-brand-cream" : ""}`}>
                          {b.label}
                        </span>
                        <span className={`font-mono ${b.neg ? "text-brand-terracotta" : b.bold ? "text-brand-gold font-semibold" : "text-brand-cream/85"}`}>
                          {b.raw ? b.v : (b.neg ? "−" : "") + fmtINRShort(b.v)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card-soft p-5 mt-4 bg-brand-sand/40 border-brand-sand">
                  <div className="text-xs uppercase tracking-[0.16em] font-semibold text-brand-terracotta">Notes for the lender pitch</div>
                  <p className="mt-2 text-sm text-brand-forest/85 leading-relaxed">{activeResult.notes}</p>
                </div>
              </aside>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Client + save */}
      <section className="mt-12 card-soft p-6 md:p-7" data-testid="elig-save-block">
        <h3 className="font-outfit font-bold text-xl text-brand-forest">Save for this client</h3>
        <p className="mt-1 text-sm text-brand-sage">Persist this assessment so you can revisit, compare or share.</p>

        <div className="mt-5 grid sm:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm font-medium">Client name *</Label>
            <Input data-testid="elig-client-name" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} className="mt-1.5" placeholder="Full name" />
          </div>
          <div>
            <Label className="text-sm font-medium">Phone</Label>
            <Input data-testid="elig-client-phone" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} className="mt-1.5" placeholder="10-digit" />
          </div>
          <div>
            <Label className="text-sm font-medium">Email</Label>
            <Input data-testid="elig-client-email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} type="email" className="mt-1.5" placeholder="optional" />
          </div>
          <div className="sm:col-span-1 flex items-end">
            <button
              onClick={save}
              disabled={saving}
              data-testid="elig-save-btn"
              className="btn-primary w-full disabled:opacity-60"
            >
              {saving ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>) : (<><Save className="h-4 w-4" /> Save</>)}
            </button>
          </div>
          <div className="sm:col-span-4">
            <Label className="text-sm font-medium">Notes</Label>
            <Textarea data-testid="elig-client-notes" value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} className="mt-1.5" placeholder="Lender thoughts, anchor relationship, what to push next..." />
          </div>
        </div>
      </section>

      {/* History */}
      <section className="mt-10" data-testid="elig-history">
        <div className="flex items-center justify-between">
          <h3 className="font-outfit font-bold text-xl text-brand-forest">Saved assessments</h3>
          <span className="text-sm text-brand-sage">{history.length} total</span>
        </div>
        {history.length === 0 ? (
          <div className="mt-6 card-soft p-10 text-center text-brand-sage" data-testid="elig-history-empty">
            Saved assessments will appear here. Calculate and save your first one above.
          </div>
        ) : (
          <ul className="mt-6 grid lg:grid-cols-2 gap-4">
            {history.map((a) => {
              const bestM = METHOD_DEFS.find((m) => m.key === a.best_method);
              return (
                <li key={a.id} data-testid={`elig-history-item-${a.id}`} className="card-soft p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-outfit font-bold text-lg text-brand-forest truncate">{a.client_name}</div>
                      <div className="mt-1 text-xs text-brand-sage">
                        {new Date(a.created_at).toLocaleString()}
                        {a.client_phone ? ` · ${a.client_phone}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => removeAssessment(a.id)}
                      data-testid={`elig-delete-${a.id}`}
                      className="text-brand-sage hover:text-red-600 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-brand-sand/40 border border-brand-sand p-3">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-brand-sage">Best method</div>
                      <div className="mt-1 font-outfit font-semibold text-brand-forest text-sm">{bestM?.short || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-brand-forest text-brand-cream p-3">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-brand-gold">Eligibility</div>
                      <div className="mt-1 font-outfit font-bold text-base">{fmtINRShort(a.best_eligibility)}</div>
                    </div>
                  </div>
                  {a.client_notes && (
                    <p className="mt-3 text-xs text-brand-sage line-clamp-2">{a.client_notes}</p>
                  )}
                  <button
                    onClick={() => loadAssessment(a)}
                    data-testid={`elig-load-${a.id}`}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-brand-terracotta hover:text-[#b25535]"
                  >
                    Load into calculator <ArrowRight className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function median(arr) {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
